import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { ArrowRight, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const BUSINESS_SIZES = ['Local business', 'Big company', 'Not sure yet'] as const

type BusinessSize = typeof BUSINESS_SIZES[number]
type FieldName = 'name' | 'email' | 'message'
type FieldErrors = Partial<Record<FieldName, string>>
type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    company: string
    businessSize: BusinessSize
    message: string
    website: string
}

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    company: '',
    businessSize: 'Local business',
    message: '',
    website: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const FIELD_ORDER: FieldName[] = ['name', 'email', 'message']

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = 'Please tell us your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (values.message.trim().length < 10) errors.message = 'Please tell us a little more about what you need.'

    return errors
}

const fieldClass = 'h-12 rounded-xl bg-white px-4 text-base md:text-base'

const QuoteForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<FormStatus>('idle')
    const [serverError, setServerError] = useState('')
    const [submittedName, setSubmittedName] = useState('')
    const successRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))
        setErrors(current => (field in current ? { ...current, [field]: undefined } : current))
    }

    const focusFirstError = (fieldErrors: FieldErrors) => {
        const first = FIELD_ORDER.find(field => fieldErrors[field])

        if (first) document.getElementById(`quote-${first}`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const nextErrors = validate(values)

        setErrors(nextErrors)
        setServerError('')

        if (Object.keys(nextErrors).length > 0) {
            focusFirstError(nextErrors)
            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/enquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => ({})) as { error?: string, fields?: FieldErrors }

            if (!response.ok) {
                if (data.fields) {
                    setErrors(data.fields)
                    focusFirstError(data.fields)
                }

                setServerError(data.error ?? 'Something went wrong on our side. Please try again.')
                setStatus('error')
                return
            }

            setSubmittedName(values.name.trim().split(/\s+/)[0])
            setValues(INITIAL_VALUES)
            setStatus('success')
        } catch {
            setServerError('We could not send your enquiry. Please check your connection and try again.')
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex min-h-[30rem] flex-col items-center justify-center text-center'>
                <span className='flex size-16 items-center justify-center rounded-full bg-volt text-ink'>
                    <CircleCheck className='size-8' aria-hidden='true' />
                </span>
                <h3 ref={successRef} tabIndex={-1} className='mt-6 font-heading text-2xl font-bold outline-none'>
                    Thanks, {submittedName}. Your enquiry is in.
                </h3>
                <p className='mt-3 max-w-sm leading-relaxed text-muted-foreground'>
                    We'll get back to you with a quote soon, so keep an eye on your inbox.
                </p>
                <Button
                    type='button'
                    variant='outline'
                    onClick={() => setStatus('idle')}
                    className='mt-8 h-11 rounded-full px-6 font-semibold'
                >
                    Send another enquiry
                </Button>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form onSubmit={handleSubmit} noValidate className='relative'>
            <h3 className='font-heading text-xl font-bold'>
                Request a quote
            </h3>
            <p className='mt-1.5 text-sm text-muted-foreground'>
                Takes about a minute. Fields marked * are required.
            </p>

            <div className='mt-8 grid gap-5 sm:grid-cols-2'>
                <div className='space-y-2'>
                    <Label htmlFor='quote-name'>
                        Your name *
                    </Label>
                    <Input
                        id='quote-name'
                        name='name'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'quote-name-error' : undefined}
                        className={fieldClass}
                    />
                    {errors.name && (
                        <p id='quote-name-error' className='text-sm font-medium text-destructive'>
                            {errors.name}
                        </p>
                    )}
                </div>

                <div className='space-y-2'>
                    <Label htmlFor='quote-email'>
                        Email address *
                    </Label>
                    <Input
                        id='quote-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={errors.email ? 'quote-email-error' : undefined}
                        className={fieldClass}
                    />
                    {errors.email && (
                        <p id='quote-email-error' className='text-sm font-medium text-destructive'>
                            {errors.email}
                        </p>
                    )}
                </div>

                <div className='space-y-2 sm:col-span-2'>
                    <Label htmlFor='quote-company'>
                        Business name <span className='font-normal text-muted-foreground'>(optional)</span>
                    </Label>
                    <Input
                        id='quote-company'
                        name='company'
                        autoComplete='organization'
                        value={values.company}
                        onChange={update('company')}
                        className={fieldClass}
                    />
                </div>

                <fieldset className='sm:col-span-2'>
                    <legend className='text-sm font-medium'>
                        Your business is
                    </legend>
                    <div className='mt-2 grid gap-2 sm:grid-cols-3'>
                        {BUSINESS_SIZES.map(size => {
                            const selected = values.businessSize === size

                            return (
                                <label
                                    key={size}
                                    className={cn(
                                        'flex h-12 cursor-pointer items-center justify-center rounded-xl border px-3 text-sm font-semibold transition-colors has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/40',
                                        selected ? 'border-ink bg-ink text-paper' : 'border-input bg-white text-ink hover:border-ink/40'
                                    )}
                                >
                                    <input
                                        type='radio'
                                        name='businessSize'
                                        value={size}
                                        checked={selected}
                                        onChange={() => setValues(current => ({ ...current, businessSize: size }))}
                                        className='sr-only'
                                    />
                                    {size}
                                </label>
                            )
                        })}
                    </div>
                </fieldset>

                <div className='space-y-2 sm:col-span-2'>
                    <Label htmlFor='quote-message'>
                        What do you need? *
                    </Label>
                    <Textarea
                        id='quote-message'
                        name='message'
                        rows={5}
                        value={values.message}
                        onChange={update('message')}
                        placeholder='For example: a five-page website for our salon, with prices, a gallery and online booking.'
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={errors.message ? 'quote-message-error' : undefined}
                        className='min-h-36 rounded-xl bg-white px-4 py-3 text-base md:text-base'
                    />
                    {errors.message && (
                        <p id='quote-message-error' className='text-sm font-medium text-destructive'>
                            {errors.message}
                        </p>
                    )}
                </div>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
                <label htmlFor='quote-website'>
                    Website
                </label>
                <input
                    id='quote-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {serverError && (
                <p role='alert' className='mt-6 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive'>
                    {serverError}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs leading-relaxed text-muted-foreground sm:max-w-[16rem]'>
                    We only use your details to reply to your enquiry.
                </p>
                <Button
                    type='submit'
                    size='xl'
                    disabled={submitting}
                    className='w-full sm:w-auto'
                >
                    {submitting ? (
                        <>
                            <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send enquiry
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}

export default QuoteForm
